import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
export const Route = createFileRoute('/_layout/case-studies/')({
    component: CaseStudiesPage,
});
function CaseStudiesPage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            {/* Intro */}
            <div className="prose prose-neutral max-w-none">
                <h1>Case studies</h1>

                <p>
                    Real examples of how businesses use <Link to="/what-is-adora">Adora Analytics</Link> to measure
                    offline and online campaign performance — from printed menus to physical placements.
                </p>
            </div>

            <Separator className="my-8"/>

            {/* Case study list */}
            <div className="grid gap-6 sm:grid-cols-2">
                {/* Beef & Basil */}
                <Card className="flex flex-col">
                    <CardHeader className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Badge variant="secondary">Restaurant</Badge>
                            <Badge variant="outline">Offline-first</Badge>
                        </div>
                        <CardTitle>Beef &amp; Basil</CardTitle>
                    </CardHeader>

                    <CardContent className="flex flex-col justify-between gap-4">
                        <p className="text-sm text-muted-foreground">
                            A local restaurant using Adora to understand how menus, flyers, and physical placements
                            perform in the real world — replacing guesswork with data.
                        </p>

                        <Link to="/case-studies/beef-and-basil" className="text-sm font-medium text-primary hover:underline">
                            Read case study →
                        </Link>
                    </CardContent>
                </Card>

                {/* Coming soon */}
                <Card className="border-dashed">
                    <CardHeader>
                        <CardTitle className="text-muted-foreground">More case studies coming</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                        Additional case studies will be published as more businesses use Adora in real-world campaigns
                        across different industries.
                    </CardContent>
                </Card>
            </div>

            <Separator className="my-12"/>

            {/* Learn more */}
            <LearnMore />
        </main>);
}
const LearnMore = () => (<section className="mx-auto max-w-6xl px-6 md:px-8 py-12">
        <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-semibold">Learn more about Adora</h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
                Understand how Adora works, what makes it different, and how to use it effectively for offline and
                online campaigns.
            </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <LearnCard title="Offline marketing analytics" description="How to measure real-world campaigns beyond clicks and impressions." to="/learn/offline-marketing-analytics"/>

            <LearnCard title="Smart Links" description="What Smart Links are and how they work as measurable entry points." to="/learn/smart-links"/>

            <LearnCard title="How Adora works" description="A practical overview of campaigns, tracking, and reporting." to="/how-it-works"/>

            <LearnCard title="Pricing" description="Simple, transparent pricing with no long-term commitments." to="/pricing"/>
        </div>
    </section>);
LearnMore.displayName = 'LearnMore';
const LearnCard = ({ title, description, to }) => (<Card className="h-full transition hover:shadow-md hover:bg-muted/30">
        <CardHeader>
            <CardTitle className="text-base md:text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col justify-between gap-3">
            <p className="text-sm text-muted-foreground">{description}</p>
            <Link to={to} className="text-sm font-medium text-primary hover:underline w-fit" aria-label={title}>
                Learn more →
            </Link>
        </CardContent>
    </Card>);
LearnCard.displayName = 'LearnCard';
